import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface IssueReport {
  type: string;
  category: string;
  location: string;
  priority: string;
  title: string;
  description: string;
  date: string;
}

const ReportIssues = () => {
  const routes = all_routes;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [issueData, setIssueData] = useState<IssueReport>({
    type: "",
    category: "",
    location: "",
    priority: "medium",
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
  });

  const issueTypes = [
    {
      value: "technical",
      label: "Technical Issue",
      icon: "ti-computer",
      color: "primary",
    },
    {
      value: "facility",
      label: "Facility Problem",
      icon: "ti-building",
      color: "warning",
    },
    {
      value: "equipment",
      label: "Equipment Malfunction",
      icon: "ti-tool",
      color: "danger",
    },
    {
      value: "safety",
      label: "Safety Concern",
      icon: "ti-shield-x",
      color: "danger",
    },
  ];

  const technicalCategories = [
    "Computer/Laptop Issues",
    "Internet Connectivity",
    "Projector/Display Problems",
    "Audio/Sound System",
    "Software Issues",
    "Network Access",
    "Printer Problems",
    "Other Technical",
  ];

  const facilityCategories = [
    "Lighting Issues",
    "HVAC/Temperature",
    "Plumbing Problems",
    "Electrical Issues",
    "Cleaning/Maintenance",
    "Security Systems",
    "Furniture/Fixtures",
    "Other Facility",
  ];

  const equipmentCategories = [
    "Smart Board/Interactive Display",
    "Laboratory Equipment",
    "Sports Equipment",
    "Office Equipment",
    "Classroom Furniture",
    "Other Equipment",
  ];

  const safetyCategories = [
    "Fire Safety",
    "Emergency Systems",
    "Structural Issues",
    "Hazardous Materials",
    "Security Concerns",
    "Other Safety",
  ];

  const locations = [
    "Classroom 101",
    "Classroom 102",
    "Classroom 103",
    "Science Lab 1",
    "Science Lab 2",
    "Computer Lab",
    "Library",
    "Gymnasium",
    "Auditorium",
    "Staff Room",
    "Principal Office",
    "Administration",
    "Cafeteria",
    "Playground",
    "Parking Area",
    "Other",
  ];

  const priorities = [
    {
      value: "low",
      label: "Low Priority",
      color: "info",
      description: "Can wait",
    },
    {
      value: "medium",
      label: "Medium Priority",
      color: "warning",
      description: "Should be fixed soon",
    },
    {
      value: "high",
      label: "High Priority",
      color: "danger",
      description: "Needs immediate attention",
    },
    {
      value: "emergency",
      label: "Emergency",
      color: "danger",
      description: "Safety risk - urgent",
    },
  ];

  const getCategories = () => {
    switch (issueData.type) {
      case "technical":
        return technicalCategories;
      case "facility":
        return facilityCategories;
      case "equipment":
        return equipmentCategories;
      case "safety":
        return safetyCategories;
      default:
        return [];
    }
  };

  const handleInputChange = (field: keyof IssueReport, value: string) => {
    setIssueData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "type" ? { category: "" } : {}),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !issueData.type ||
      !issueData.category ||
      !issueData.location ||
      !issueData.title ||
      !issueData.description
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    console.log("Submitting issue report:", issueData);
    setShowSuccessModal(true);

    setTimeout(() => {
      setIssueData({
        type: "",
        category: "",
        location: "",
        priority: "medium",
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
      });
    }, 2000);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Report Technical Issues</h3>
              <p className="text-muted mb-0">Report facility & tech problems</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="row mb-4">
            {issueTypes.map((type) => (
              <div key={type.value} className="col-lg-3 col-md-6 mb-3">
                <div
                  className={`card bg-${type.color}-light h-100 cursor-pointer hover-card`}
                  onClick={() => handleInputChange("type", type.value)}
                >
                  <div className="card-body text-center">
                    <div className={`avatar avatar-lg bg-${type.color} mb-2`}>
                      <i className={`ti ${type.icon} text-white fs-2`}></i>
                    </div>
                    <h6>{type.label}</h6>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Report Form */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="ti ti-alert-triangle me-2 text-warning"></i>Issue
                Report Form
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label">
                      Issue Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={issueData.type}
                      onChange={(e) =>
                        handleInputChange("type", e.target.value)
                      }
                      required
                    >
                      <option value="">Select issue type...</option>
                      {issueTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={issueData.category}
                      onChange={(e) =>
                        handleInputChange("category", e.target.value)
                      }
                      disabled={!issueData.type}
                      required
                    >
                      <option value="">Select category...</option>
                      {getCategories().map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Location <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={issueData.location}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      required
                    >
                      <option value="">Select location...</option>
                      {locations.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Priority Level</label>
                    <select
                      className="form-select"
                      value={issueData.priority}
                      onChange={(e) =>
                        handleInputChange("priority", e.target.value)
                      }
                    >
                      {priorities.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label} - {priority.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      Issue Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Brief summary of the issue..."
                      value={issueData.title}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      Detailed Description{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={6}
                      placeholder="Provide detailed information about the issue, when it occurred, and any steps you've already taken..."
                      value={issueData.description}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      required
                    ></textarea>
                  </div>
                  <div className="col-12">
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-danger">
                        <i className="ti ti-send me-2"></i>Submit Report
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setIssueData({
                            type: "",
                            category: "",
                            location: "",
                            priority: "medium",
                            title: "",
                            description: "",
                            date: new Date().toISOString().split("T")[0],
                          })
                        }
                      >
                        <i className="ti ti-refresh me-2"></i>Reset Form
                      </button>
                      <Link
                        to={routes.teacherDashboard}
                        className="btn btn-outline-secondary"
                      >
                        <i className="ti ti-arrow-left me-2"></i>Cancel
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Emergency Contacts */}
          <div className="card mt-4">
            <div className="card-header">
              <h6 className="card-title mb-0 text-danger">
                <i className="ti ti-phone me-2"></i>Emergency Contacts
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="d-flex align-items-center p-3 bg-danger-light rounded">
                    <div className="avatar avatar-sm bg-danger me-3">
                      <i className="ti ti-shield-x text-white"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Safety Emergency</h6>
                      <p className="mb-0">
                        <a href="tel:911" className="text-danger fw-bold">
                          911
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center p-3 bg-warning-light rounded">
                    <div className="avatar avatar-sm bg-warning me-3">
                      <i className="ti ti-tool text-white"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Maintenance Hotline</h6>
                      <p className="mb-0">
                        <a
                          href="tel:+1234567890"
                          className="text-warning fw-bold"
                        >
                          +1 (234) 567-8900
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Response Times */}
          <div className="card mt-4">
            <div className="card-body">
              <h6 className="mb-3">Expected Response Times</h6>
              <div className="row text-center">
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-danger">
                    <i className="ti ti-alert-triangle fs-2"></i>
                    <h6 className="mt-2">Emergency</h6>
                    <p className="mb-0 small text-muted">Immediate</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-warning">
                    <i className="ti ti-clock fs-2"></i>
                    <h6 className="mt-2">High Priority</h6>
                    <p className="mb-0 small text-muted">Within 4 hours</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-primary">
                    <i className="ti ti-calendar fs-2"></i>
                    <h6 className="mt-2">Medium Priority</h6>
                    <p className="mb-0 small text-muted">1-2 business days</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-info">
                    <i className="ti ti-calendar-plus fs-2"></i>
                    <h6 className="mt-2">Low Priority</h6>
                    <p className="mb-0 small text-muted">3-5 business days</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title text-success">
                  <i className="ti ti-check-circle me-2"></i>Issue Reported
                  Successfully
                </h5>
              </div>
              <div className="modal-body text-center">
                <div className="avatar avatar-xl bg-success-transparent mb-3">
                  <i className="ti ti-tool fs-1 text-success"></i>
                </div>
                <h6>Your issue has been reported to the maintenance team.</h6>
                <p className="text-muted mb-0">
                  You will receive updates on the resolution progress via email.
                  Ticket ID will be sent shortly.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center">
                <button
                  className="btn btn-success"
                  onClick={() => setShowSuccessModal(false)}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportIssues;
